interface Props {
  number: number;
  total: number;
  title: string;
  sketch?: string;
  /** The scene's newest saved draft, while it is not final. */
  draft?: string;
  failed?: boolean;
  /** Offered when the scene failed, so playback is not held at it for good. */
  onSkip?: () => void;
}

/**
 * Direction B (beta only): a scene that is not built yet, in the video's own frame. Its plan first, then its newest
 * draft marked as not final, so nothing moves when the finished scene lands in the same box.
 */
export function StudioBuildFrameB({ number, total, title, sketch, draft, failed, onSkip }: Props) {
  if (draft && !failed) {
    return (
      <div data-build-frame className="relative h-full w-full bg-b-panel">
        <img src={draft} alt={`Scene ${number}, newest draft`} className="absolute inset-0 h-full w-full object-cover" />
        <span className="absolute left-3 top-3 rounded-[var(--b-radius-chip)] bg-black/65 px-2 py-1 text-[12px] font-medium text-white">Draft · not final</span>
      </div>
    );
  }
  return (
    <div data-build-frame className="flex h-full w-full flex-col justify-center gap-[2.5cqh] bg-b-panel px-[8%]">
      <span className="text-[12px] font-semibold uppercase tracking-[0.12em] text-b-t3">Scene {number} of {total} · From the plan</span>
      <p className="font-display text-[clamp(20px,4.2cqw,44px)] font-semibold leading-tight text-b-t1">{title}</p>
      {sketch && <p className="max-w-[60ch] text-[clamp(14px,1.9cqw,20px)] leading-relaxed text-b-t2">{sketch}</p>}
      <span className="flex items-center gap-2 text-[13px] text-b-t3">
        <span aria-hidden="true" className={failed ? 'h-2 w-2 rounded-full bg-destructive' : 'h-2 w-2 rounded-full bg-primary motion-safe:animate-pulse'} />
        {failed ? 'This scene needs attention' : 'Ivy is building this scene'}
      </span>
      {onSkip && (
        <button
          type="button"
          onClick={onSkip}
          className="self-start rounded-[var(--b-radius-control)] px-3 py-1.5 text-[13px] font-medium text-b-t1 shadow-[inset_0_0_0_1px_hsl(var(--b-line-strong))] transition-colors hover:bg-b-raised"
        >
          Skip this scene
        </button>
      )}
    </div>
  );
}
